import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  FileText, RefreshCw, Check, X, Eye, Edit2, Trash2, 
  Image, Send, ExternalLink, ChevronDown, ChevronUp, Sparkles, Upload, Copy
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import * as api from '../../services/api';

const DraftsManager = () => {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [processing, setProcessing] = useState(null);
  const [editObservation, setEditObservation] = useState('');
  const [imagePrompt, setImagePrompt] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  const fetchDrafts = useCallback(async () => {
    try {
      const res = await api.getDraftDossiers();
      setDrafts(res.drafts || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDrafts();
  }, [fetchDrafts]);

  const handlePublish = async (dossierId) => {
    setProcessing(dossierId);
    try {
      await api.publishDossier(dossierId);
      
      // Remove from drafts list immediately (optimistic update)
      setDrafts(prev => prev.filter(d => d.dossier_id !== dossierId));
      setSelectedDraft(null);
      
      alert('✅ Artículo publicado correctamente');
      
      // Refresh in background to sync
      fetchDrafts();
    } catch (error) {
      alert('⚠️ Error: ' + (error.response?.data?.detail || error.message));
      // Restore list on error
      fetchDrafts();
    } finally {
      setProcessing(null);
    }
  };

  const handleDelete = async (dossierId) => {
    if (!window.confirm('¿Eliminar este artículo?')) return;
    setProcessing(dossierId);
    
    try {
      await api.deleteDossier(dossierId);
      
      // Remove from drafts list immediately (optimistic update)
      setDrafts(prev => prev.filter(d => d.dossier_id !== dossierId));
      setSelectedDraft(null);
      
      // Refresh in background to sync
      fetchDrafts();
    } catch (error) {
      alert('⚠️ Error: ' + (error.response?.data?.detail || error.message));
      // Restore list on error
      fetchDrafts();
    } finally {
      setProcessing(null);
    }
  };

  const handleRegenerateImage = async (dossierId, customPrompt = null, referenceImages = []) => {
    setProcessing(dossierId + '_img');
    try {
      let res;
      if (referenceImages && referenceImages.length > 0) {
        res = await api.regenerateDossierImageWithRefs(dossierId, customPrompt || null, referenceImages);
      } else {
        res = await api.regenerateDossierImage(dossierId, customPrompt || null);
      }
      
      if (res.success) {
        // Update image immediately in UI
        if (selectedDraft?.dossier_id === dossierId) {
          setSelectedDraft(prev => ({ ...prev, cover_image: res.cover_image }));
        }
        
        setImagePrompt('');
        alert('✅ Imagen regenerada correctamente');
        
        // Refresh list in background to sync
        fetchDrafts();
      }
    } catch (error) {
      alert('⚠️ Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setProcessing(null);
    }
  };

  const handleEditWithAI = async (dossierId) => {
    if (!editObservation.trim()) {
      alert('Escribe una observación para la IA');
      return;
    }
    
    // Set processing state but DON'T block UI
    setProcessing(dossierId + '_edit');
    
    try {
      const res = await api.editDossierWithAI(dossierId, editObservation);
      if (res.success) {
        // Clear the input immediately for better UX
        setEditObservation('');
        
        // Show non-blocking success message
        alert('✅ Artículo editado correctamente. Refrescando...');
        
        // Fetch updates in parallel
        const [_, updated] = await Promise.all([
          fetchDrafts(),
          api.getDossier(dossierId)
        ]);
        
        setSelectedDraft(updated);
      }
    } catch (error) {
      alert('⚠️ Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setProcessing(null);
    }
  };

  const handleGetSuggestions = async (dossierId) => {
    // Set processing state but DON'T block UI
    setProcessing(dossierId + '_suggestions');
    setShowSuggestions(true);
    
    try {
      const res = await api.getArticleSuggestions(dossierId);
      if (res.suggestions) {
        setSuggestions(res.suggestions);
        // Insert the generated prompt directly into the edit field
        if (res.suggestions[0] && res.suggestions[0].prompt) {
          setEditObservation(res.suggestions[0].prompt);
        }
      }
    } catch (error) {
      alert('⚠️ Error obteniendo sugerencias: ' + (error.response?.data?.detail || error.message));
      setShowSuggestions(false);
    } finally {
      setProcessing(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Artículos Pendientes de Revisión
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {drafts.length} artículos esperando aprobación
          </p>
        </div>
        <Button onClick={fetchDrafts} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualizar
        </Button>
      </div>

      {drafts.length === 0 ? (
        <div className="bg-white rounded-xl border p-8 text-center">
          <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No hay artículos pendientes de revisión</p>
        </div>
      ) : (
        <div className="space-y-3">
          {drafts.map((draft) => (
            <DraftCard
              key={draft.dossier_id}
              draft={draft}
              onView={() => setSelectedDraft(draft)}
              onPublish={() => handlePublish(draft.dossier_id)}
              onDelete={() => handleDelete(draft.dossier_id)}
              processing={processing === draft.dossier_id}
            />
          ))}
        </div>
      )}

      {/* Draft Preview Modal */}
      {selectedDraft && (
        <DraftPreviewModal
          draft={selectedDraft}
          onClose={() => { setSelectedDraft(null); setEditObservation(''); setImagePrompt(''); setShowSuggestions(false); setSuggestions([]); }}
          onPublish={() => handlePublish(selectedDraft.dossier_id)}
          onDelete={() => handleDelete(selectedDraft.dossier_id)}
          onRegenerateImage={(customPrompt) => handleRegenerateImage(selectedDraft.dossier_id, customPrompt)}
          onEditWithAI={() => handleEditWithAI(selectedDraft.dossier_id)}
          onGetSuggestions={() => handleGetSuggestions(selectedDraft.dossier_id)}
          editObservation={editObservation}
          setEditObservation={setEditObservation}
          imagePrompt={imagePrompt}
          setImagePrompt={setImagePrompt}
          processing={processing}
          showSuggestions={showSuggestions}
          suggestions={suggestions}
        />
      )}
    </div>
  );
};

const DraftCard = ({ draft, onView, onPublish, onDelete, processing }) => (
  <div className="bg-white rounded-xl border p-4">
    <div className="flex gap-4">
      {/* Image preview */}
      <div className="w-24 h-24 flex-shrink-0 bg-gray-100 rounded-lg overflow-hidden">
        {draft.cover_image ? (
          <img src={draft.cover_image} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Image className="w-8 h-8 text-gray-300" />
          </div>
        )}
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <Badge className="bg-yellow-100 text-yellow-700">Borrador</Badge>
          <Badge variant="outline">{draft.category || draft.sector}</Badge>
        </div>
        <h3 className="font-semibold line-clamp-2 mb-1">{draft.title}</h3>
        <p className="text-sm text-gray-500 line-clamp-1">
          {draft.executive_summary?.[0] || draft.content?.slice(0, 100)}
        </p>
        <p className="text-xs text-gray-400 mt-2">
          Creado: {new Date(draft.created_at).toLocaleString('es-MX')}
        </p>
      </div>
      
      <div className="flex flex-col gap-2">
        <Button variant="outline" size="sm" onClick={onView}>
          <Eye className="w-4 h-4" />
        </Button>
        <Button size="sm" className="bg-green-600" onClick={onPublish} disabled={processing}>
          {processing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
        </Button>
        <Button variant="outline" size="sm" className="text-red-600" onClick={onDelete}>
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  </div>
);

const DraftPreviewModal = ({ 
  draft, onClose, onPublish, onDelete, onRegenerateImage, 
  onEditWithAI, onGetSuggestions, editObservation, setEditObservation, 
  imagePrompt, setImagePrompt, processing, showSuggestions, suggestions 
}) => {
  const [showSources, setShowSources] = useState(false);
  const [showImageOptions, setShowImageOptions] = useState(false);
  const [loadingSuggestion, setLoadingSuggestion] = useState(false);
  const [referenceImages, setReferenceImages] = useState([]);
  const fileInputRef = useRef(null);
  
  const handleSuggestPrompt = async () => {
    setLoadingSuggestion(true);
    try {
      const res = await api.suggestImagePrompt(draft.dossier_id);
      if (res.success && res.suggestion) {
        setImagePrompt(res.suggestion);
      }
    } catch (error) {
      console.error('Error getting suggestion:', error);
    } finally {
      setLoadingSuggestion(false);
    }
  };
  
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const imageFiles = files.filter(f => f.type.startsWith('image/'));
    setReferenceImages(prev => [...prev, ...imageFiles].slice(0, 3)); // Max 3 images
  };
  
  const removeRefImage = (index) => {
    setReferenceImages(prev => prev.filter((_, i) => i !== index));
  };
  
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Vista Previa del Artículo</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Cover Image */}
          <div className="relative">
            <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
              {draft.cover_image ? (
                <img src={draft.cover_image} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Image className="w-16 h-16 text-gray-300" />
                </div>
              )}
            </div>
            
            {/* Image Regeneration Controls */}
            <div className="absolute bottom-2 right-2 flex flex-col items-end gap-2">
              {showImageOptions && (
                <div className="bg-white rounded-lg shadow-lg p-3 w-96">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs text-gray-500">
                      Instrucciones para la imagen (opcional):
                    </p>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 px-2 text-xs text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                      onClick={handleSuggestPrompt}
                      disabled={loadingSuggestion}
                    >
                      {loadingSuggestion ? (
                        <RefreshCw className="w-3 h-3 animate-spin mr-1" />
                      ) : (
                        <Sparkles className="w-3 h-3 mr-1" />
                      )}
                      Sugerir
                    </Button>
                  </div>
                  <Textarea
                    value={imagePrompt}
                    onChange={(e) => setImagePrompt(e.target.value)}
                    placeholder="Ej: Una imagen de un político dando un discurso en un congreso, tonos azules..."
                    rows={2}
                    className="text-sm mb-2"
                  />
                  
                  {/* Reference Images Upload */}
                  <div className="mb-3">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs text-gray-500">Imágenes de referencia (opcional):</p>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 px-2 text-xs"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Upload className="w-3 h-3 mr-1" />
                        Adjuntar
                      </Button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={handleFileSelect}
                      />
                    </div>
                    {referenceImages.length > 0 && (
                      <div className="flex gap-2 flex-wrap">
                        {referenceImages.map((file, idx) => (
                          <div key={idx} className="relative w-16 h-16 rounded overflow-hidden border">
                            <img 
                              src={URL.createObjectURL(file)} 
                              alt="" 
                              className="w-full h-full object-cover"
                            />
                            <button
                              onClick={() => removeRefImage(idx)}
                              className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {referenceImages.length === 0 && (
                      <p className="text-[10px] text-gray-400">Máx. 3 imágenes para usar como referencia visual</p>
                    )}
                  </div>
                  
                  <div className="flex gap-2">
                    <Button 
                      size="sm"
                      onClick={() => {
                        onRegenerateImage(imagePrompt, referenceImages);
                        setShowImageOptions(false);
                        setReferenceImages([]);
                      }}
                      disabled={processing === draft.dossier_id + '_img'}
                    >
                      {processing === draft.dossier_id + '_img' ? (
                        <RefreshCw className="w-4 h-4 animate-spin mr-1" />
                      ) : (
                        <Image className="w-4 h-4 mr-1" />
                      )}
                      Generar
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => {
                        setShowImageOptions(false);
                        setReferenceImages([]);
                      }}
                    >
                      Cancelar
                    </Button>
                  </div>
                </div>
              )}
              <Button 
                size="sm" 
                variant="outline"
                className="bg-white"
                onClick={() => setShowImageOptions(!showImageOptions)}
                disabled={processing === draft.dossier_id + '_img'}
              >
                {processing === draft.dossier_id + '_img' ? (
                  <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Image className="w-4 h-4 mr-2" />
                )}
                Regenerar Imagen
              </Button>
              
              {/* Suggestions Button */}
              <Button 
                size="sm"
                variant="outline"
                className="bg-white border-purple-300 text-purple-700 hover:bg-purple-50"
                onClick={onGetSuggestions}
                disabled={processing === draft.dossier_id + '_suggestions'}
              >
                {processing === draft.dossier_id + '_suggestions' ? (
                  <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" />
                )}
                Sugerencias IA
              </Button>
            </div>
          </div>
          
          {/* Title & Meta */}
          <div>
            <Badge className="mb-2">{draft.category || draft.sector}</Badge>
            <h1 className="text-2xl font-bold">{draft.title}</h1>
          </div>
          
          {/* Content Preview */}
          <div className="prose max-w-none">
            <p className="text-lg text-gray-600 font-medium border-l-4 border-red-600 pl-4">
              {draft.executive_summary?.[0] || draft.content?.replace(/<[^>]+>/g, '').slice(0, 200)}
            </p>
            
            <div 
              className="mt-6 space-y-4 text-gray-800 leading-relaxed article-content"
              dangerouslySetInnerHTML={{ 
                __html: draft.content?.includes('<p>') 
                  ? draft.content 
                  : draft.content?.split('\n\n').map(p => `<p>${p.trim()}</p>`).join('') || ''
              }}
            />
          </div>
          
          {/* Analysis */}
          {(draft.who_benefits || draft.who_loses) && (
            <div className="grid md:grid-cols-2 gap-4">
              {draft.who_benefits && (
                <div className="p-4 bg-green-50 rounded-lg border border-green-100">
                  <h4 className="font-bold text-green-800 mb-2">Quién se Beneficia</h4>
                  <p className="text-green-900 text-sm">{draft.who_benefits}</p>
                </div>
              )}
              {draft.who_loses && (
                <div className="p-4 bg-red-50 rounded-lg border border-red-100">
                  <h4 className="font-bold text-red-800 mb-2">Quién se Perjudica</h4>
                  <p className="text-red-900 text-sm">{draft.who_loses}</p>
                </div>
              )}
            </div>
          )}
          
          {draft.editorial_take && (
            <div className="p-4 bg-gray-900 text-white rounded-lg">
              <h4 className="font-bold text-yellow-400 mb-2">Nuestra Perspectiva</h4>
              <p className="text-gray-200 italic">"{draft.editorial_take}"</p>
            </div>
          )}
          
          {/* Sources */}
          <div className="border-t pt-4 space-y-4">
            <button 
              onClick={() => setShowSources(!showSources)}
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
            >
              {showSources ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              Fuentes Consultadas ({(draft.sources?.length || 0)})
            </button>
            
            {showSources && (
              <div className="space-y-4">
                {/* Search Query Used */}
                {draft.search_query && (
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs font-medium text-gray-500 mb-1">Búsqueda realizada:</p>
                    <p className="text-sm text-gray-700 italic">"{draft.search_query}"</p>
                  </div>
                )}
                
                {/* Firecrawl Sources */}
                {draft.sources?.length > 0 && (
                  <div className="bg-orange-50 p-3 rounded-lg border border-orange-100">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-6 h-6 bg-orange-600 rounded flex items-center justify-center">
                        <span className="text-white text-xs font-bold">F</span>
                      </div>
                      <h5 className="font-semibold text-orange-800">Firecrawl ({draft.sources.length})</h5>
                      <span className="text-xs text-orange-600 bg-orange-100 px-2 py-0.5 rounded">Contexto Web</span>
                    </div>
                    <ul className="space-y-1">
                      {draft.sources.map((src, i) => (
                        <li key={i} className="text-sm">
                          <a href={src.url} target="_blank" rel="noopener noreferrer" className="text-orange-700 hover:underline flex items-start gap-1">
                            <ExternalLink className="w-3 h-3 mt-1 flex-shrink-0" />
                            <span>{src.title || src.url}</span>
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* No sources message */}
                {!draft.sources?.length && (
                  <p className="text-sm text-gray-500 italic">No se encontraron fuentes externas para este artículo.</p>
                )}
              </div>
            )}
          </div>
          
          {/* Edit with AI */}
          <div className="border-t pt-4">
            <h4 className="font-medium mb-2">Editar con IA</h4>
            <p className="text-sm text-gray-500 mb-2">
              Escribe una observación y la IA editará el artículo según tus indicaciones.
            </p>
            <Textarea
              value={editObservation}
              onChange={(e) => setEditObservation(e.target.value)}
              placeholder="Ej: Agrega más contexto sobre el impacto económico, hazlo más crítico con el gobierno..."
              rows={3}
            />
            <div className="flex gap-2 mt-2">
              <Button 
                onClick={onEditWithAI}
                disabled={!editObservation.trim() || processing === draft.dossier_id + '_edit'}
              >
                {processing === draft.dossier_id + '_edit' ? (
                  <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Edit2 className="w-4 h-4 mr-2" />
                )}
                Aplicar Edición
              </Button>
              
              <Button 
                variant="outline"
                className="border-purple-300 text-purple-700 hover:bg-purple-50"
                onClick={onGetSuggestions}
                disabled={processing === draft.dossier_id + '_suggestions'}
              >
                {processing === draft.dossier_id + '_suggestions' ? (
                  <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" />
                )}
                Sugerencias IA
              </Button>
            </div>
          </div>
          
          {/* AI Suggestions */}
          {showSuggestions && (
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-600" />
                Recomendaciones de IA
              </h4>
              <p className="text-sm text-gray-600 mb-3">
                El prompt se ha insertado automáticamente arriba. Revisa las recomendaciones y haz clic en "Aplicar Edición".
              </p>
              {suggestions.length > 0 ? (
                <div className="space-y-3">
                  {suggestions.map((suggestion, idx) => (
                    <div key={idx} className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                      <div className="flex items-start gap-2">
                        <span className="bg-purple-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                          {idx + 1}
                        </span>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-purple-900">{suggestion.title}</p>
                          <p className="text-sm text-gray-700 mt-1">{suggestion.description}</p>
                          {suggestion.example && (
                            <p className="text-xs text-gray-500 italic mt-1">Ej: {suggestion.example}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 text-purple-600 animate-spin" />
                  <span className="ml-2 text-gray-600">Generando sugerencias...</span>
                </div>
              )}
            </div>
          )}
        </div>
        
        <DialogFooter className="flex gap-2 mt-4">
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
          <Button variant="outline" className="text-red-600" onClick={onDelete}>
            <Trash2 className="w-4 h-4 mr-2" />
            Eliminar
          </Button>
          <Button className="bg-green-600" onClick={onPublish} disabled={processing === draft.dossier_id}>
            {processing === draft.dossier_id ? (
              <RefreshCw className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Check className="w-4 h-4 mr-2" />
            )}
            Publicar Artículo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DraftsManager;
